import { PageViewTracker } from '@belong/common';
import type { GetStaticProps } from 'next';
import { ReviewsPage } from 'src/features/reviews/components/reviews-page/reviews-page';
import { reviewsLayout } from 'src/features/reviews/components/reviews-page/reviews-layout';
import { ReviewsSeo } from 'src/features/reviews/components/reviews-page/reviews-seo';
import {
  getReviewFeedPage,
  getReviewPageCount,
} from 'src/features/reviews/reviews.queries';
import {
  REVALIDATE_SECONDS,
  RETRY_REVALIDATE_SECONDS,
  emptyFeedPage,
  isBuildPhase,
} from 'src/features/reviews/reviews.static';
import type { ReviewFeedPage } from 'src/features/reviews/reviews.utils';

type ReviewsProps = {
  feedPage: ReviewFeedPage;
  totalPages: number;
};

/**
 * `/reviews` — every review of Belong, pooled from the review sites, newest
 * first, as a feed over a WebGL town that sinks out of frame as the reader
 * scrolls.
 *
 * The first forty-five are rendered at build time so the opening screen is
 * crawlable and readable with no JavaScript; the rest are a page away, at
 * `/reviews/page/2` and on, which is what makes the whole set reachable
 * without running any.
 */
export default function Reviews({ feedPage, totalPages }: ReviewsProps) {
  return (
    <PageViewTracker name="reviews">
      <ReviewsSeo feedPage={feedPage} totalPages={totalPages} />
      <ReviewsPage feedPage={feedPage} />
    </PageViewTracker>
  );
}

export const getStaticProps: GetStaticProps<ReviewsProps> = async () => {
  try {
    const [feedPage, totalPages] = await Promise.all([
      getReviewFeedPage(1),
      getReviewPageCount(),
    ]);

    return { props: { feedPage, totalPages }, revalidate: REVALIDATE_SECONDS };
  } catch (error) {
    console.error('[reviews] could not read the first page of the feed', error);

    if (!isBuildPhase()) {
      // A revalidation, so **rethrow**: Next then discards the attempt and
      // keeps serving the last page that succeeded, retrying on a later
      // request. Answering empty props here instead is what let a two-minute
      // Omni blip publish an empty `/reviews` over a good one — the
      // regeneration looked like a success, so Next replaced the page with it.
      throw error;
    }

    // The initial build is the one case with no previous page to keep, and a
    // throw here fails the build of the whole site over one section's
    // warehouse being briefly unreachable. So the page ships its empty state
    // and asks to be regenerated in a minute rather than in an hour.
    return {
      props: { feedPage: emptyFeedPage(1), totalPages: 1 },
      revalidate: RETRY_REVALIDATE_SECONDS,
    };
  }
};

Reviews.getLayout = reviewsLayout;
